import { injectCountEndPoint, portalAPI } from './portalAPI';

import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import {
    ComplianceBlockHistoryDto,
    ComplianceListItem,
    ComplianceSummaryDto,
    PartyComplianceCheckCreateDto,
    PartyComplianceCheckDto,
    PartyComplianceCheckUpdateDto,
} from 'types/compliance.ts';
import {
    BusinessAreaDto,
    ContactDataCreateOrUpdateCompanyDto,
    ContactDataCreateOrUpdatePersonDto,
    ContactDto,
    ContactListItem,
    ContactMinDto,
    ContactMinDtoWithAddresses,
    ContactMinDtoWithRelationsDto,
    FuzzySearchResultDto,
    PartyGroupingStrategy,
    PartyType,
    UpdatePartyAddressDto,
    UpdatePartyEmailDto,
    UpdatePartyMobileDto,
} from 'types/contacts';
import { PartyMergePreviewDto, PartyMergeRequestDto } from 'types/contactMerge';
import { HistoryDto, HistoryEntityDto, HistoryEntityType, HistoryFieldType, UpdateHistoryDto } from 'types/histories';
import { PaginationProps, QueryResponse } from 'types/paginationAndFilter';
import { objectToQueryParams } from 'utils/linkUtil';
import { logError } from 'utils/monitoring';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

// Args parsed from a serialized RTK Query cache key; concrete shape varies per endpoint.
type ParsedQueryArgs = Record<string, unknown>;

// Derive the slice state from the actual reducer rather than importing from
// `@reduxjs/toolkit/dist/...` internals, which are not semver-stable.
type PortalApiSliceState = { portalAPI: ReturnType<typeof portalAPI.reducer> };
type GetPortalApiState = () => PortalApiSliceState;
type AnyThunkDispatch = ThunkDispatch<PortalApiSliceState, unknown, AnyAction>;

// Helper function to update all cached getContactsWithFilters queries
const updateAllContactsWithFiltersCaches = (
    getState: GetPortalApiState,
    dispatch: AnyThunkDispatch,
    updateRecipe: (args: ParsedQueryArgs) => (draft: QueryResponse<ContactListItem>) => void
) => {
    const state = getState();
    Object.keys(state.portalAPI.queries).forEach((key) => {
        if (key.startsWith('getContactsWithFilters')) {
            const match = key.match(/getContactsWithFilters\((.*)\)/);
            if (match) {
                try {
                    const args = JSON.parse(match[1]);
                    dispatch(APIContactsSlice.util.updateQueryData('getContactsWithFilters', args, updateRecipe(args)));
                } catch (e) {
                    logError(e);
                }
            }
        }
    });
};

// Helper function to update all cached getMinContactsWithAddressesListWithFilters queries
const updateAllMinContactsWithAddressesCaches = (
    getState: GetPortalApiState,
    dispatch: AnyThunkDispatch,
    updateRecipe: (args: ParsedQueryArgs) => (draft: QueryResponse<ContactMinDtoWithAddresses>) => void
) => {
    const state = getState();
    Object.keys(state.portalAPI.queries).forEach((key) => {
        if (key.startsWith('getMinContactsWithAddressesListWithFilters')) {
            const match = key.match(/getMinContactsWithAddressesListWithFilters\((.*)\)/);
            if (match) {
                try {
                    const args = JSON.parse(match[1]);
                    dispatch(
                        APIContactsSlice.util.updateQueryData(
                            'getMinContactsWithAddressesListWithFilters',
                            args,
                            updateRecipe(args)
                        )
                    );
                } catch (e) {
                    logError(e);
                }
            }
        }
    });
};

// Helper function to update all cached searchContacts queries
const updateAllSearchContactsCaches = (
    getState: GetPortalApiState,
    dispatch: AnyThunkDispatch,
    updateRecipe: (args: ParsedQueryArgs) => (draft: QueryResponse<ContactMinDtoWithAddresses>) => void
) => {
    const state = getState();
    Object.keys(state.portalAPI.queries).forEach((key) => {
        if (key.startsWith('searchContacts')) {
            const match = key.match(/searchContacts\((.*)\)/);
            if (match) {
                try {
                    const args = JSON.parse(match[1]);
                    dispatch(APIContactsSlice.util.updateQueryData('searchContacts', args, updateRecipe(args)));
                } catch (e) {
                    logError(e);
                }
            }
        }
    });
};

// Party save can create/archive notifications server-side
// (partner/manager changes, AML case block warnings), so refetch them instantly
const partyUpdatedTags = (id: number) =>
    [
        'Contacts',
        'PartiesMin',
        'Parties',
        'Notifications',
        { type: 'Party', id: String(id) },
        {
            type: 'HistoryListItems',
            historySelector: {
                entityType: HistoryEntityType.PARTY,
                entityId: id,
                fieldType: HistoryFieldType.RELATIONSHIP_MANAGER_EMPLOYEE,
            },
        },
        {
            type: 'HistoryListItems',
            historySelector: {
                entityType: HistoryEntityType.PARTY,
                entityId: id,
                fieldType: HistoryFieldType.RELATIONSHIP_PARTNER_EMPLOYEE,
            },
        },
    ] as const;

export const APIContactsSlice = portalAPI
    .enhanceEndpoints({
        addTagTypes: [
            'Contacts',
            'ContactsCount',
            'Party',
            'PartiesMin',
            'Parties',
            'Compliances',
            'HistoryListItems',
            'PartyEmployeeHistory',
            'Matter',
            'MattersMin',
            'MattersListItem',
            'InvoicesListItem',
            'Notifications',
        ],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getContactById: builder.query<ContactDto, string>({
                query: (id) => `/v1/parties/${id}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) => [{ type: 'Party', id: arg }],
            }),
            // The personal code behind the asterisks the detail reads while obfuscated, for the one view that shows it.
            getContactPersonalCode: builder.query<{ value: string | null }, string>({
                query: (id) => `/v1/parties/${id}/personal-code`,
                keepUnusedDataFor: 0,
            }),
            getContactEmployeeHistory: builder.query<HistoryDto[], { id: string; type: string }>({
                query: ({ id, type }) => `/v1/parties/${id}/employees/${type}`,
                keepUnusedDataFor: 0,
                providesTags: (_1, _2, arg) =>
                    arg.id && arg.type ? [{ type: 'PartyEmployeeHistory', id: arg.id }] : [],
            }),
            getMinContactsListWithFilters: accumulatedQueryWithFilters<ContactMinDto>(builder, '/v1/parties/min', {
                providesTags: ['PartiesMin'],
            }),
            getMinContactsWithAddressesListWithFilters: accumulatedQueryWithFilters<ContactMinDtoWithAddresses>(
                builder,
                '/v1/parties/min-with-addresses',
                {
                    providesTags: ['PartiesMin'],
                }
            ),
            getMinContactsWithRelationsListWithFilters: builder.query<
                QueryResponse<ContactMinDtoWithRelationsDto>,
                PaginationProps & {
                    prioritizeEmployeesOf?: number[];
                    prioritizeCompaniesOf?: number[];
                    prioritizeParties?: number[];
                    groupingStrategy?: PartyGroupingStrategy;
                }
            >({
                query: ({
                    filters = '',
                    passiveFilters = '',
                    sort = '',
                    page = 0,
                    pageSize = 10,
                    prioritizeEmployeesOf,
                    prioritizeCompaniesOf,
                    prioritizeParties,
                    groupingStrategy,
                }) => {
                    const queryParams: Record<string, unknown> = {
                        sort,
                        page,
                        size: pageSize,
                    };
                    const body: Record<string, unknown> = {
                        filter: passiveFilters ? [passiveFilters, filters].filter((item) => item).join(';') : filters,
                    };
                    if (prioritizeEmployeesOf) {
                        body.prioritizeEmployeesOf = prioritizeEmployeesOf;
                    }
                    if (prioritizeCompaniesOf) {
                        body.prioritizeCompaniesOf = prioritizeCompaniesOf;
                    }
                    if (prioritizeParties) {
                        body.prioritizeParties = prioritizeParties;
                    }
                    if (groupingStrategy) {
                        body.groupingStrategy = groupingStrategy;
                    }
                    return {
                        url: `/v1/parties/min-with-relations${objectToQueryParams(queryParams)}`,
                        method: 'POST',
                        body,
                    };
                },
                merge: (currentCache, newItems) => {
                    if (newItems.first) {
                        return newItems;
                    }
                    return { ...newItems, content: [...currentCache.content, ...newItems.content] };
                },
                // `page` is intentionally stripped so all pages share a single cache entry.
                serializeQueryArgs: ({ endpointName, queryArgs: { page, ...rest } }) => {
                    return `${endpointName}(${JSON.stringify(rest)})`;
                },
                forceRefetch: ({ currentArg, previousArg }) => {
                    return currentArg !== previousArg;
                },
                providesTags: ['PartiesMin'],
            }),
            getContactsWithFilters: accumulatedQueryWithFilters<ContactListItem>(builder, '/v1/parties', {
                providesTags: ['Contacts'],
            }),
            getCompliancesWithFilters: accumulatedQueryWithFilters<ComplianceListItem>(
                builder,
                '/v1/parties/compliances',
                {
                    providesTags: ['Compliances'],
                }
            ),
            getComplianceStatistics: builder.query<ComplianceSummaryDto, void>({
                query: () => '/v1/parties/compliances/statistics',
                providesTags: ['Compliances'],
            }),
            getContactsCount: injectCountEndPoint(builder, '/v1/parties', 'ContactsCount'),
            addContact: builder.mutation<
                ContactDto,
                ContactDataCreateOrUpdatePersonDto | ContactDataCreateOrUpdateCompanyDto
            >({
                // `id` is intentionally stripped from the body on create.
                query: ({ id, ...body }) => ({
                    url: `/v1/parties`,
                    method: 'POST',
                    body,
                }),
                // `Party`: the page of the employer a new person is added to lists its people by the ids it holds.
                invalidatesTags: ['Contacts', 'PartiesMin', 'Parties', 'Party'],
            }),
            updateContact: builder.mutation<
                ContactDto,
                ContactDataCreateOrUpdatePersonDto | ContactDataCreateOrUpdateCompanyDto
            >({
                query: ({ id, ...body }) => ({
                    url: `/v1/parties/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? partyUpdatedTags(arg.id)
                        : ['Contacts', 'PartiesMin', 'Parties', 'HistoryListItems', 'Notifications'],
            }),
            getContactMergePreview: builder.query<PartyMergePreviewDto, number[]>({
                query: (partyIds) => `/v1/parties/merge-preview?partyIds=${partyIds.join(',')}`,
                keepUnusedDataFor: 0,
            }),
            // The contacts the merge wizard compares, in the order given. It provides no Party tag on purpose: the
            // merge invalidates the kept contact's, and a refetch would 404 on the deleted merged-away ones.
            getContactsForMerge: builder.query<ContactDto[], number[]>({
                queryFn: async (ids, _api, _extraOptions, baseQuery) => {
                    const results = await Promise.all(ids.map((id) => baseQuery(`/v1/parties/${id}`)));
                    const failed = results.find((result) => result.error);
                    return failed?.error
                        ? { error: failed.error }
                        : { data: results.map((result) => result.data as ContactDto) };
                },
                keepUnusedDataFor: 0,
            }),
            mergeContacts: builder.mutation<ContactDto, PartyMergeRequestDto & { keptPartyId: number }>({
                query: ({ keptPartyId, ...body }) => ({
                    url: `/v1/parties/${keptPartyId}/merge`,
                    method: 'POST',
                    body,
                }),
                // Not the merged-away parties' tags: they are deleted, so a refetch would 404 into the
                // global /404 redirect. Their cases and invoices now name the kept party as client or payer,
                // and their compliance data moved onto the kept party, hence the three list tags.
                invalidatesTags: (_1, _2, arg) => [
                    ...partyUpdatedTags(arg.keptPartyId),
                    'MattersListItem',
                    'InvoicesListItem',
                    'Compliances',
                ],
            }),
            changeContactType: builder.mutation<ContactDto, { id: number; targetPartyType: PartyType }>({
                query: ({ id, targetPartyType }) => ({
                    url: `/v1/parties/${id}/change-type`,
                    method: 'PUT',
                    body: { targetPartyType },
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [{ type: 'Party', id: String(arg.id) }, 'Contacts', 'PartiesMin', 'Parties']
                        : ['Contacts', 'PartiesMin', 'Parties'],
            }),
            generatePartyNumber: builder.mutation<ContactDto, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/parties/${id}/generate-party-number`,
                    method: 'PUT',
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [{ type: 'Party', id: arg.id }, 'Contacts', 'PartiesMin', 'Parties']
                        : ['Contacts', 'PartiesMin', 'Parties'],
            }),
            updateContactEmployeeHistory: builder.mutation<HistoryDto, HistoryEntityDto & UpdateHistoryDto>({
                query: ({ id, entityId, ...body }) => ({
                    url: `/v1/parties/${entityId}/employees/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.entityId
                        ? [
                              { type: 'PartyEmployeeHistory', id: arg.entityId },
                              { type: 'Party', id: String(arg.entityId) },
                          ]
                        : ['PartyEmployeeHistory'],
            }),
            updateContactComplianceCheck: builder.mutation<PartyComplianceCheckDto, PartyComplianceCheckUpdateDto>({
                query: ({ partyId, ...body }) => ({
                    url: `/v1/parties/${partyId}/compliances/${body.id}`,
                    method: 'PUT',
                    body,
                }),
                // resolving/breaking a check reconciles AML blocks and can create/archive
                // AML case block warning notifications
                invalidatesTags: ['Notifications'],
            }),
            addContactComplianceCheck: builder.mutation<PartyComplianceCheckDto, PartyComplianceCheckCreateDto>({
                query: ({ partyId, ...body }) => ({
                    url: `/v1/parties/${partyId}/compliances`,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['Notifications'],
            }),
            getComplianceBlockHistory: builder.query<ComplianceBlockHistoryDto[], string>({
                query: (partyId) => `/v1/parties/${partyId}/compliance-blocks`,
                providesTags: (_1, _2, arg) => [{ type: 'Party', id: arg }],
            }),
            acknowledgeComplianceBlock: builder.mutation<void, { partyId: number; blockId: number; comment?: string }>({
                query: ({ partyId, blockId, comment }) => ({
                    url: `/v1/parties/${partyId}/compliance-blocks/${blockId}/acknowledge`,
                    method: 'POST',
                    body: comment ? { comment } : undefined,
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'Party', id: String(arg.partyId) },
                    'Contacts',
                    'Compliances',
                    'Notifications',
                ],
            }),
            deleteContactEmployeeHistoryEntry: builder.mutation<undefined, HistoryEntityDto>({
                query: ({ entityId, id }) => ({
                    url: `/v1/parties/${entityId}/employees/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: (_1, _2, arg) => [
                    { type: 'PartyEmployeeHistory', id: arg.entityId },
                    { type: 'Party', id: String(arg.entityId) },
                ],
            }),
            deleteContact: builder.mutation<ContactDto, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/parties/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Contacts', 'PartiesMin', 'Parties'],
            }),
            updatePartyMainEmail: builder.mutation<ContactDto, UpdatePartyEmailDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/parties/${id}/main-email`,
                    method: 'PUT',
                    body,
                }),
                async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
                    const { data: updatedRecord } = await queryFulfilled;

                    dispatch(
                        APIContactsSlice.util.updateQueryData('getContactById', id.toString(), () => updatedRecord)
                    );
                    updateAllContactsWithFiltersCaches(getState, dispatch, () => (draft) => {
                        const item = draft.content?.find((contact: ContactListItem) => contact.id === id);
                        if (item) {
                            item.mainEmail = updatedRecord.mainEmail;
                        }
                    });
                },
            }),
            updatePartyMainMobile: builder.mutation<ContactDto, UpdatePartyMobileDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/parties/${id}/main-mobile`,
                    method: 'PUT',
                    body,
                }),
                async onQueryStarted({ id }, { dispatch, queryFulfilled, getState }) {
                    const { data: updatedRecord } = await queryFulfilled;
                    dispatch(
                        APIContactsSlice.util.updateQueryData('getContactById', id.toString(), () => updatedRecord)
                    );
                    updateAllContactsWithFiltersCaches(getState, dispatch, () => (draft) => {
                        const item = draft.content?.find((contact: ContactListItem) => contact.id === id);
                        if (item) {
                            item.mainMobile = updatedRecord.mainMobile;
                        }
                    });
                },
            }),
            updatePartyAddress: builder.mutation<ContactDto, UpdatePartyAddressDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/parties/${id}/address`,
                    method: 'PUT',
                    body,
                }),
                async onQueryStarted({ id, addressType }, { dispatch, queryFulfilled, getState }) {
                    const { data: updatedRecord } = await queryFulfilled;
                    dispatch(
                        APIContactsSlice.util.updateQueryData('getContactById', id.toString(), () => updatedRecord)
                    );
                    const addressUpdateRecipe =
                        () => (draft: QueryResponse<ContactListItem> | QueryResponse<ContactMinDtoWithAddresses>) => {
                            const item = draft.content?.find(
                                (contact: ContactListItem | ContactMinDtoWithAddresses) => contact.id === id
                            );
                            if (item) {
                                if (updatedRecord.partyType === PartyType.PERSON) {
                                    switch (addressType) {
                                        case 'BILLING':
                                            item.billingAddress = updatedRecord.billingAddress;
                                            break;
                                        case 'CORRESPONDENCE':
                                            item.correspondenceAddress = updatedRecord.correspondenceAddress;
                                            break;
                                        case 'HOME':
                                        default:
                                            item.homeAddress = updatedRecord.homeAddress;
                                            break;
                                    }
                                } else if (updatedRecord.partyType === PartyType.COMPANY) {
                                    switch (addressType) {
                                        case 'VISITING':
                                            item.visitingAddress = updatedRecord.visitingAddress;
                                            break;
                                        case 'BILLING':
                                            item.billingAddress = updatedRecord.billingAddress;
                                            break;
                                        case 'CORRESPONDENCE':
                                            item.correspondenceAddress = updatedRecord.correspondenceAddress;
                                            break;
                                        case 'REGISTRATION':
                                        default:
                                            item.registrationAddress = updatedRecord.registrationAddress;
                                            break;
                                    }
                                }
                            }
                        };
                    updateAllContactsWithFiltersCaches(getState, dispatch, addressUpdateRecipe);
                    updateAllSearchContactsCaches(getState, dispatch, addressUpdateRecipe);
                    updateAllMinContactsWithAddressesCaches(getState, dispatch, addressUpdateRecipe);
                },
            }),
            getContactBusinessAreaList: builder.query<BusinessAreaDto[], string>({
                query: (ids) => `/v1/parties/${ids}/business-areas`,
                keepUnusedDataFor: 0,
            }),
            fuzzySearchParties: builder.query<
                FuzzySearchResultDto[],
                { searchTerm: string; searchType?: 'NAME' | 'REGISTRATION_NUMBER' | 'PERSONAL_ID'; threshold?: number }
            >({
                query: ({ searchTerm, searchType = 'NAME', threshold }) => ({
                    url: `/v1/parties/fuzzy`,
                    params: {
                        searchTerm,
                        searchType,
                        threshold,
                    },
                }),
                keepUnusedDataFor: 0,
            }),
            searchContacts: builder.query<
                QueryResponse<ContactMinDtoWithAddresses>,
                { query: string; partyType?: PartyType; page?: number; pageSize?: number }
            >({
                query: ({ query, partyType, page = 0, pageSize = 10 }) => ({
                    url: `/v1/parties/search`,
                    params: {
                        query,
                        partyType,
                        page,
                        size: pageSize,
                    },
                }),
                merge: (currentCache, newItems) => {
                    if (newItems.first) {
                        return newItems;
                    }
                    return { ...newItems, content: [...currentCache.content, ...newItems.content] };
                },
                // `page` is intentionally stripped so all pages share a single cache entry.
                serializeQueryArgs: ({ endpointName, queryArgs: { page, ...rest } }) => {
                    return `${endpointName}(${JSON.stringify(rest)})`;
                },
                forceRefetch: ({ currentArg, previousArg }) => {
                    return currentArg !== previousArg;
                },
                providesTags: ['PartiesMin'],
            }),
        }),
        overrideExisting: false,
    });

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetContactByIdQuery,
    useGetContactEmployeeHistoryQuery,
    useLazyGetContactPersonalCodeQuery,
    useUpdateContactEmployeeHistoryMutation,
    useDeleteContactEmployeeHistoryEntryMutation,
    useLazyGetCompliancesWithFiltersQuery,
    useGetComplianceStatisticsQuery,
    useLazyGetContactByIdQuery,
    useLazyGetMinContactsListWithFiltersQuery,
    useLazyGetMinContactsWithAddressesListWithFiltersQuery,
    useLazyGetMinContactsWithRelationsListWithFiltersQuery,
    useAddContactMutation,
    useUpdateContactMutation,
    useGeneratePartyNumberMutation,
    useChangeContactTypeMutation,
    useDeleteContactMutation,
    useUpdatePartyMainEmailMutation,
    useUpdatePartyMainMobileMutation,
    useUpdatePartyAddressMutation,
    useLazyGetContactsWithFiltersQuery,
    useLazyGetContactsCountQuery,
    useGetContactBusinessAreaListQuery,
    useUpdateContactComplianceCheckMutation,
    useAddContactComplianceCheckMutation,
    useGetComplianceBlockHistoryQuery,
    useAcknowledgeComplianceBlockMutation,
    useLazyFuzzySearchPartiesQuery,
    useLazySearchContactsQuery,
    useGetContactMergePreviewQuery,
    useGetContactsForMergeQuery,
    useMergeContactsMutation,
} = APIContactsSlice;
